import { t } from "@i18n";

/**
 * PUT a file to a presigned URL, reporting progress.
 *
 * Deliberately XHR, not the app's axios: axios carries core-platform defaults
 * (bearer token, a stray `withCredentials` header) that must not reach S3, and
 * `fetch` has no upload progress.
 *
 * Resolves only when the PUT returns 2xx — reaching 100 % of the bytes sent is
 * not the same as the object existing in the bucket.
 *
 * @param {string} uploadUrl
 * @param {File|Blob} file
 * @param {{
 *   contentType?: string,
 *   onProgress?: (event: { loaded: number, total: number }) => void,
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<void>}
 * @throws {DOMException} `AbortError` when `signal` aborts
 */
export function putFileWithProgress(uploadUrl, file, { contentType, onProgress, signal } = {}) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException("Upload aborted", "AbortError"));
            return;
        }

        const xhr = new XMLHttpRequest();

        function handleAbort() {
            xhr.abort();
        }

        function cleanup() {
            signal?.removeEventListener("abort", handleAbort);
        }

        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader("Content-Type", contentType || file.type || "application/octet-stream");

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) onProgress?.({ loaded: event.loaded, total: event.total });
        };

        xhr.onload = () => {
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
                return;
            }
            reject(new Error(t("content:upload.putFailed", { status: xhr.status })));
        };

        xhr.onerror = () => {
            cleanup();
            reject(new Error(t("content:upload.putFailed", { status: xhr.status })));
        };

        xhr.onabort = () => {
            cleanup();
            reject(new DOMException("Upload aborted", "AbortError"));
        };

        signal?.addEventListener("abort", handleAbort);
        xhr.send(file);
    });
}
